import { Component, OnInit, ViewChild, ElementRef, Output, EventEmitter } from '@angular/core';
import { UntypedFormControl } from '@angular/forms';
import { BorrowerService } from 'app/services/borrower/borrower.service';
import { Borrower } from 'app/models/dto/borrower.model';
import { MatMenuTrigger, MatMenu } from '@angular/material/menu';
import { map, take } from 'rxjs/operators';
import * as _ from 'lodash';
import { PhonePipe } from 'app/pipes/phone/phone.pipe';
import { Select } from '@ngxs/store';
import { LenderUserState } from 'app/store/lender-user/lender-user/lender-user.state';
import { Observable } from 'rxjs';
import { LenderUser } from 'app/models/dto/lender-user.model';

interface SearchResult {
  borrower: Borrower;
  matchedAttr: {
    label: string;
    value: any;
  } | null;
}

@Component({
  selector: 'app-borrower-search-input',
  templateUrl: './borrower-search-input.component.html',
  styleUrls: ['./borrower-search-input.component.scss'],
  standalone: false
})
export class BorrowerSearchInputComponent implements OnInit {
  @Select(LenderUserState.lenderUser) lenderUser$: Observable<LenderUser>;
  @Output() borrower = new EventEmitter<Borrower>();
  @ViewChild('input', { static: true }) input: ElementRef;
  @ViewChild(MatMenuTrigger, { static: true }) trigger: MatMenuTrigger;
  @ViewChild(MatMenu, { static: true }) menu: MatMenu;
  searchControl = new UntypedFormControl();
  results: SearchResult[] = [];
  loading: boolean;
  defaultAll: boolean;

  constructor(
    private borrowerService: BorrowerService,
    private phonePipe: PhonePipe,
  ) {}

  ngOnInit() {
    this.lenderUser$.pipe(
      map(currentUser => currentUser.permissions.includes('defaultAllBorrowerSearch'))
    ).subscribe(permitted => this.defaultAll = permitted);
  }

  clearInput() {
    setTimeout(() => {
      this.trigger.closeMenu();
      this.results = [];
      this.searchControl.setValue('');
      this.input.nativeElement.focus();
    }, 300);
  }

  searchValue() {
    return this.searchControl.value;
  }

  handleKeyup() {
    this.trigger.closeMenu();
    this.input.nativeElement.focus();
  }

  handleSubmit() {
    if (this.searchControl.value === null) {
      return;
    }
    let rawSearch = this.searchControl.value.trim();
    const search = rawSearch.replace(/^.*:/, '').trim(); // "all:"

    // check for minimum length
    if (search.length < 3) {
      return;
    }

    // default "all:""
    if (this.defaultAll && !/^all:/.test(rawSearch)) {
      rawSearch = 'all:' + rawSearch;
    }

    // fetch results
    this.loading = true;
    this.borrowerService.search(rawSearch).pipe(take(1)).subscribe(borrowers => {
      this.results = borrowers.map(borrower => ({
          borrower,
          status: borrower.status.replace(/([A-Z])/g, ' $1').replace(/^./, x => x.toUpperCase()),
          matchedAttr: this.matchedAttr(search, borrower)
        }));
      this.loading = false;
      this.trigger.openMenu();
      this.input.nativeElement.focus();
    });
  }

  emitBorrower(borrower: Borrower) {
    this.borrower.emit(borrower);
    this.input.nativeElement.value = borrower.name;
  }

  matchedAttr(search: string, borrower: Borrower): { label: string, value: any } | null {
    // contact email
    const contactEmail = _.get(borrower, 'primary_contact.email', '') || '';
    if (this.matches(search, contactEmail)) {
      return { label: 'Contact Email', value: contactEmail };
    }

    // contact name
    const contactFirst = _.get(borrower, 'primary_contact.first', '') || '';
    const contactLast = _.get(borrower, 'primary_contact.last', '') || '';
    const contactFull = [contactFirst, contactLast].filter(x => !!x).join(' ');
    if (
      this.matches(search, contactFirst)
      || this.matches(search, contactLast)
      || this.matches(search, contactFull)
    ) {
      return { label: 'Contact Name', value: contactFull };
    }

    // borrower id
    const borrowerId = borrower.id;
    if (search === borrowerId.toString()) {
      return { label: 'Borrower ID', value: borrowerId };
    }

    // borrower phone
    const businessPhone = _.get(borrower, 'phone', '') || '';
    if (this.matches(search, businessPhone)) {
      return { label: 'Business Phone', value: this.phonePipe.transform(businessPhone, '($1) $2-$3') };
    }

    // contact workPhone
    const workPhone = _.get(borrower, 'primary_contact.workPhone', '') || '';
    if (this.matches(search, workPhone)) {
      return { label: 'Work Phone', value: this.phonePipe.transform(workPhone, '($1) $2-$3') };
    }

    // contact mobilePhone
    const mobilePhone = _.get(borrower, 'primary_contact.mobilePhone', '') || '';
    if (this.matches(search, mobilePhone)) {
      return { label: 'Mobile Phone', value: this.phonePipe.transform(mobilePhone, '($1) $2-$3') };
    }

    return null;
  }

  matches(search: string, value: string) {
    const pattern = /[^a-z0-9]/gi;
    return value
      .replace(pattern, '')
      .indexOf(
        search.replace(pattern, '')
      ) !== -1;
  }
}
